import { readCachedEntitlement } from "./coldStart";
import { SIGNUP_BONUS_BUCKET, TOP_UP_BUCKET, bucketRemaining, } from "./credits.types";
import { userAccess } from "./userAccessPort";
export const permissionsSelector = (state) => state.permissionsReducer;
export const creditsStatusSelector = (state) => state.permissionsReducer.status;
export const creditsTotalSelector = (state) => state.permissionsReducer.total;
export const hasUnlimitedCreditsSelector = (state) => state.permissionsReducer.total === "unlimited";
/** Unix seconds at which the daily allowance refills, if the backend sent one. */
export const dailyResetsAtSelector = (state) => {
    const daily = state.permissionsReducer.buckets?.daily;
    return daily && !daily.unlimited ? daily.resetsAt : undefined;
};
export const bucketsStaleSelector = (state) => state.permissionsReducer.bucketsStale;
/**
 * Whether a balance has actually been received. Distinguishes "this user has
 * nothing left" from "we don't know yet" — before the first `/credits`, after a
 * failed one, and in the window where `resetCredits()` has emptied the slice.
 */
export const creditsBalanceLoadedSelector = (state) => state.permissionsReducer.total !== null ||
    state.permissionsReducer.buckets !== null;
/** Credits left in today's refreshing allowance. Undefined on unlimited tiers. */
export const dailyRemainingSelector = (state) => {
    const daily = state.permissionsReducer.buckets?.daily;
    return daily && !daily.unlimited ? daily.remaining : undefined;
};
/**
 * How many credits the daily grant is worth. Per-user, so never assumed.
 *
 * Undefined whenever there is no grant to report: an unlimited tier, a
 * logged-out visitor (no `/credits` call), or before the balance has arrived.
 * Copy degrades to a number-free phrasing rather than guess — see creditsCopy.
 */
export const dailyLimitSelector = (state) => {
    const daily = state.permissionsReducer.buckets?.daily;
    return daily && !daily.unlimited ? daily.limit : undefined;
};
/**
 * Purchased top-up balance. The backend omits the bucket entirely when it is 0,
 * so absence and zero mean the same thing: no top-ups.
 */
export const topUpRemainingSelector = (state) => bucketRemaining(state.permissionsReducer.buckets?.[TOP_UP_BUCKET]);
export const hasTopUpsSelector = (state) => topUpRemainingSelector(state) > 0;
/**
 * Size of the sign-up bonus the user was granted, for "2 of 5 left" copy.
 *
 * The backend took over via `limit` (ADR 0027), which it has to: the grant is
 * A/B tested at 3 vs 5 now, so a client-side constant cannot be right for
 * everyone. The fallback only covers buckets minted before it was recorded,
 * whose real size is not recoverable — 20 is a guess for those, and stale for
 * any granted after the 20 → 5 cut.
 */
export const SIGNUP_BONUS_GRANT = 20;
export const signupBonusGrantSelector = (state) => {
    const bucket = state.permissionsReducer.buckets?.[SIGNUP_BONUS_BUCKET];
    const limit = bucket && !("unlimited" in bucket) ? bucket.limit : undefined;
    return limit ?? SIGNUP_BONUS_GRANT;
};
/** Credits left of the sign-up bonus. */
export const signupBonusRemainingSelector = (state) => bucketRemaining(state.permissionsReducer.buckets?.[SIGNUP_BONUS_BUCKET]);
/**
 * Whether the user has ever been granted a sign-up bonus.
 *
 * The bucket is dropped from the response once empty, and accounts predating
 * the credit system never had one, so an absent bucket is genuinely ambiguous:
 * "spent it" and "never had one" look identical. Copy that would say "you've
 * used your sign-up credits" stays hidden rather than tell the second group
 * about a gift they never got.
 */
export const hasSignupBonusBucketSelector = (state) => state.permissionsReducer.buckets?.[SIGNUP_BONUS_BUCKET] !== undefined;
/**
 * Whether credits must stay invisible.
 *
 * A new user is given a sign-up bonus and is meant to spend it without ever
 * learning that credits exist — the first mention of them is the intro shown
 * when that bonus runs out. Every surface that would name, count or price a
 * credit consults this: the nav chip, and (DEV-3387) per-feature cost badges.
 *
 * Display only. Gates, the 402 path and the out-of-credits dialog are unaffected
 * — a user holding bonus credits has a positive balance, so they can't be
 * refused anyway.
 */
export const creditsAreSilentSelector = (state) => signupBonusRemainingSelector(state) > 0;
/**
 * Whether credits may be shown at all: the nav-bar balance, and every
 * per-feature cost badge (DEV-3387).
 *
 * Four cases say no, each because a number would be a lie: logged out (no
 * balance exists), an unlimited tier (nothing is ever debited), before
 * `GET /credits` answers, and while the sign-up bonus is unspent — that user is
 * not supposed to know credits exist yet.
 *
 * One selector rather than one per surface, so the chip and the coins can never
 * disagree about whether this user has met credits.
 */
export const creditsVisibleSelector = (state) => userAccess.isLoggedIn(state) &&
    !hasUnlimitedCreditsSelector(state) &&
    !creditsAreSilentSelector(state) &&
    state.permissionsReducer.total !== null;
/** Credits a call to this feature debits, per the last `/credits` response. */
export const featureCostSelector = (featureId) => (state) => state.permissionsReducer.featureCosts[featureId] ?? 0;
/* -------------------------------------------------------------------------- */
/* Credit features                                                            */
/* -------------------------------------------------------------------------- */
const computeCreditAccess = (state, featureId) => {
    const isLoggedIn = userAccess.isLoggedIn(state);
    const status = creditsStatusSelector(state);
    const total = creditsTotalSelector(state);
    const quotedCost = state.permissionsReducer.featureCosts[featureId];
    const cost = quotedCost ?? 0;
    if (!isLoggedIn)
        return { allowed: false, reason: "requires-login", cost };
    if (total === "unlimited")
        return { allowed: true, reason: "unlimited", cost: 0 };
    // No balance yet, or a price the backend hasn't quoted — unresolved rather
    // than denied, so the UI waits instead of flashing a refusal.
    if (total === null || quotedCost === undefined) {
        return status === "rejected"
            ? { allowed: false, reason: "insufficient-credits", cost }
            : { allowed: false, reason: "loading", cost };
    }
    return total >= cost
        ? { allowed: true, reason: "credits", cost }
        : { allowed: false, reason: "insufficient-credits", cost };
};
/*
  Recomputed on every call, then compared by value — deliberately not
  createSelector.

  `isLoggedIn` reads localStorage on web (userIsLoggedInSelector checks for an
  access_token), which redux cannot see. Reselect memoizes on the state
  argument's identity and skips its inputs entirely when that argument is
  unchanged, so a token that disappears without an intervening dispatch — logging
  out in a second tab — would leave this answering "allowed" while the
  club-import check right below, and creditsVisibleSelector, correctly flip to
  logged out. The UI would disagree with itself until something dispatched.

  The verdict is four small fields, so returning the previous object whenever
  they all match gives useSelector the same reference equality memoization gave,
  without the staleness. Same trick as the interned club-import verdicts.
*/
const makeCreditAccessSelector = (featureId) => {
    let last = null;
    return (state) => {
        const next = computeCreditAccess(state, featureId);
        if (last &&
            last.allowed === next.allowed &&
            last.reason === next.reason &&
            last.cost === next.cost) {
            return last;
        }
        last = next;
        return next;
    };
};
/**
 * Whether a call to this feature is certain to be refused for want of credits.
 *
 * Deliberately narrower than `CreditAccess.allowed === false`: this is the
 * predicate that *skips the network call*, so it may only answer true when the
 * shortfall is a fact. Everything unknown falls through to the backend, which
 * is the authority on the balance:
 *
 * - logged out — anonymous calls are not metered at all, so never short;
 * - unlimited tier, or a balance that hasn't arrived yet;
 * - a feature the backend hasn't quoted a price for;
 * - a failed `/credits` — a monitoring problem must not lock users out.
 *
 * Mirrors the backend's own check (`credits.service.ts` reserve: balance <
 * cost), so a user is stopped here exactly when a 402 would have stopped them.
 */
export const isKnownInsufficientCredits = (state, featureId) => {
    if (!userAccess.isLoggedIn(state))
        return false;
    const { total, featureCosts } = state.permissionsReducer;
    if (typeof total !== "number")
        return false;
    const cost = featureCosts[featureId];
    if (cost === undefined)
        return false;
    return total < cost;
};
/* -------------------------------------------------------------------------- */
/* Club-import features                                                       */
/* -------------------------------------------------------------------------- */
/*
  Interned verdicts. A club-import verdict has no cost and no other payload, so
  there are only four possible values — returning shared frozen instances gives
  useSelector reference equality for free.

  That matters because this check reads localStorage (via the cold-start cache
  and userIsLoggedInSelector), which createSelector cannot see. Reselect
  memoizes on the state argument's identity, so a cache change with no
  intervening dispatch would return a stale verdict — putting the read in the
  inputs does not help, since identical arguments skip the inputs entirely.
  With interned results the selector can stay a plain function: always correct,
  and still stable enough not to re-render.
*/
const REQUIRES_LOGIN = Object.freeze({
    allowed: false,
    reason: "requires-login",
});
const CLUB_IMPORTED = Object.freeze({
    allowed: true,
    reason: "club-imported",
});
const CLUB_IMPORT_LOADING = Object.freeze({
    allowed: false,
    reason: "loading",
});
const REQUIRES_CLUB_IMPORT = Object.freeze({
    allowed: false,
    reason: "requires-club-import",
});
const clubImportAccess = (state) => {
    if (!userAccess.isLoggedIn(state))
        return REQUIRES_LOGIN;
    /*
      The gate is "ever imported", not "has club data right now". Only an unlink
      clears lastImportedAt (fetchPlayerCount only ever sets it), so someone who
      deleted all their players still gets in and sees an empty state rather than
      being told to import again.
    */
    if (userAccess.lastImportedAt(state))
        return CLUB_IMPORTED;
    /*
      Cold start: userIsLoggedInSelector is true the moment an access_token exists
      in localStorage, but lastImportedAt only arrives with users/profile. Without
      the two fallbacks below, every page load would briefly tell an existing
      importer to import their club.
    */
    if (readCachedEntitlement().hasImportedClub)
        return CLUB_IMPORTED;
    const userStatus = userAccess.status(state);
    if (userStatus === "idle" || userStatus === "pending")
        return CLUB_IMPORT_LOADING;
    return REQUIRES_CLUB_IMPORT;
};
/*
  One selector instance per credit feature, cached here rather than created per
  call: the value-equality latch above lives on the instance, so a fresh one per
  render would hand useSelector a new object every time and re-render on every
  store change.
*/
const creditAccessSelectors = new Map();
export const creditAccessSelector = (featureId) => {
    const cached = creditAccessSelectors.get(featureId);
    if (cached)
        return cached;
    const selector = makeCreditAccessSelector(featureId);
    creditAccessSelectors.set(featureId, selector);
    return selector;
};
/*
  Every club-import feature resolves identically — the id carries meaning for
  callers and copy, not for the check — so the id is accepted for symmetry and
  type-safety, and one shared function serves all of them.
*/
export const clubImportAccessSelector = (_featureId) => clubImportAccess;
/*
  Promo surfaces (the front-page banner) pitch the import to everyone who could
  still do one, logged out included — they're the main audience. "loading" keeps
  the pitch hidden so a returning importer never sees a flash of it.
*/
export const showClubImportPitchSelector = (state) => {
    const { reason } = clubImportAccess(state);
    return reason === "requires-club-import" || reason === "requires-login";
};
/* Imperative forms, for thunks and handlers that have `getState()`. */
export const selectCreditAccess = (state, featureId) => creditAccessSelector(featureId)(state);
export const selectClubImportAccess = (state, featureId) => clubImportAccessSelector(featureId)(state);
export const canUseCreditFeature = (state, featureId) => selectCreditAccess(state, featureId).allowed;
export const canUseClubImportFeature = (state, featureId) => selectClubImportAccess(state, featureId).allowed;
//# sourceMappingURL=permissionsSelectors.js.map